import { useEffect, useMemo } from 'react';
import { useEditor } from '@tiptap/react';
import type { Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  DiffAdded,
  DiffDeleted,
  DiffAnchor,
  computeDiffContent,
} from '@/shared/utils';

interface IUseDiffEditorProps {
  /** The old/current version of the code */
  oldCode?: string;
  /** The new/selected version of the code to compare against */
  newCode?: string;
  /** Whether the diff editor should be created (skip when not in diff mode) */
  enabled?: boolean;
  /** Render these anchors with their original text; pass a stable set. */
  discardedAnchors?: ReadonlySet<number>;
  /**
   * Extra TipTap extensions to register alongside the diff's own. For behavior
   * the diff view itself has no opinion about but a consumer needs on both its
   * editors — a decoration the surrounding page drives, say. Read once at
   * creation, so pass a stable (module-level) array.
   */
  additionalExtensions?: Extensions;
}

/**
 * Custom hook to create a TipTap editor that displays diffs between two versions.
 *
 * Computes a structure-aware diff between oldCode and newCode, showing:
 * - Removed text with red background and strikethrough
 * - Added text with green background
 * - Unchanged text normally
 */
export function useDiffEditor({
  oldCode,
  newCode,
  enabled = true,
  discardedAnchors,
  additionalExtensions,
}: IUseDiffEditorProps) {
  // Compute diff content only when enabled, this is to skip diff computation when not in diff mode.
  const diffContent = useMemo(() => {
    if (!enabled) {
      return null;
    }
    try {
      return computeDiffContent(oldCode ?? '', newCode ?? '', discardedAnchors);
    } catch (error) {
      console.warn('Error computing diff content:', error);
      return null;
    }
  }, [enabled, oldCode, newCode, discardedAnchors]);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        // `dir="auto"` per text block, matching the non-diff AOP editor: the
        // browser resolves each block from its own first strong directional
        // character, so an RTL-authored automation reads right-to-left while
        // English blocks around it stay LTR. On the container instead of the
        // blocks it would resolve once for the whole document.
        paragraph: {
          HTMLAttributes: {
            dir: 'auto',
          },
        },
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
          HTMLAttributes: {
            class: 'markdown-heading',
            dir: 'auto',
          },
        },
        bold: {
          HTMLAttributes: {
            class: 'font-semibold',
          },
        },
        italic: {
          HTMLAttributes: {
            class: 'italic',
          },
        },
        strike: {
          HTMLAttributes: {
            class: 'line-through',
          },
        },
        code: {
          HTMLAttributes: {
            class: 'markdown-inline-code',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'markdown-code-block',
            dir: 'auto',
          },
        },
        blockquote: {
          HTMLAttributes: {
            class: 'markdown-blockquote',
            dir: 'auto',
          },
        },
        horizontalRule: {
          HTMLAttributes: {
            class: 'markdown-hr',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'list-item',
            dir: 'auto',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal list-outside ms-4 ps-2 my-1',
            dir: 'auto',
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc list-outside ms-4 ps-2 my-1',
            dir: 'auto',
          },
        },
      }),
      // Custom diff marks
      DiffAdded,
      DiffDeleted,
      // Adds data-diff-anchor to block nodes for scroll-to-change navigation
      DiffAnchor,
      // Table support. `renderWrapper` emits the `.tableWrapper` div around the
      // table — with `resizable: false` there's no TableView node view to add it
      // otherwise, and a bare table is what `overflow-x-auto` (styles.css) hangs
      // off. Without it a table wider than the panel pushes past the card and
      // scrolls the whole document sideways instead of just the table, which is
      // how the non-diff AOP editor already behaves.
      Table.configure({
        resizable: false,
        renderWrapper: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: 'Loading...',
        emptyEditorClass: 'is-editor-empty',
      }),
      ...(additionalExtensions ?? []),
    ],
    content: '',
    editable: false,
    editorProps: {
      attributes: {
        // `diff-view` scopes the de-emphasized/unchanged-text styling in
        // styles.css to diff rendering only — the regular editable preview
        // uses the same `tiptap-editor-container` class and must stay at
        // full-strength text color.
        class:
          'w-full h-full min-h-full p-8 xl:p-12 focus:outline-none focus:ring-0 tiptap-editor-container diff-view',
      },
    },
  });

  // Update editor content when diff content changes
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync editor content when diff computation changes
  useEffect(() => {
    if (!editor) {
      return;
    }

    if (diffContent) {
      editor.commands.setContent(diffContent, { emitUpdate: true });
    } else {
      editor.commands.clearContent(true);
    }
  }, [editor, diffContent]);

  return editor;
}
